import {Injectable} from '@angular/core';
import {FormGroup, NgForm} from '@angular/forms';
import {flatMap, hasGruppeTechnischeSteuerung, ListControl, UIArtefakt, UiBaseControl, UiInputControl, UIMenu} from '../models/uiArtefakt';
import {BehaviorSubject, Subscription} from 'rxjs';
import {EventService} from './event.service';
import {Meldung} from '../models/meldung';
import {take, tap} from 'rxjs/operators';
import {EnumGruppeTechnischeSteuerung} from '../models/enumGruppeTechnischeSteuerung';
import {getAllMenuStateSeiten} from '../models/menuState';

interface ValidationState {
  menuSeite: string;
  identifikator: string;
  artefaktForm: NgForm;
  meldung: Meldung[];
}

@Injectable()
export class ValidationService {
  public calcFormsValid$ = new BehaviorSubject<boolean>(false);
  private _validationState: ValidationState[] = [];
  private _formStatusSubscriptions: Map<string, Subscription> = new Map();
  private _menu: UIMenu;

  public constructor(private _eventService: EventService) {
    this._eventService.menu$.subscribe(menu => {
      this._menu = menu;
      this._validationState.forEach(validationStateItem => {
        for (const gruppe of this._menu.UIMenuGruppen) {
          for (const seite of gruppe.UIMenuSeiten) {
            for (const artefakt of seite.UIArtefakt) {
              const foundArtefakt = this._findKey(artefakt as UiInputControl, validationStateItem.identifikator);
              if (!!foundArtefakt) {
                validationStateItem.meldung = foundArtefakt.UIMeldung;
                return;
              }
            }
          }
        }
      });
      this._validationState.filter(validationStateItem => Object.keys(validationStateItem.artefaktForm.form.controls).length === 0)
          .forEach(validationStateItem => this._cleanUpDeletedArtefact(validationStateItem));
      this._updateValidationState();
    });
    this._eventService.validateSeiteSelected$.pipe(
        tap(selectedSeite => {
          const menuGruppe = this._menu.UIMenuGruppen.filter(gruppe => gruppe.Identifikator === selectedSeite)[0];
          if (!!menuGruppe) {
            menuGruppe.UIMenuSeiten.forEach(menuSeite => {
              this._validationState.filter(validationState => validationState.menuSeite === menuSeite.Identifikator)
              .forEach(validationState => this.markControlsDirtyAndTouched(validationState.artefaktForm.form));
            });
          } else {
            this._validationState.filter(validationState => validationState.menuSeite === selectedSeite)
            .forEach(validationState => this.markControlsDirtyAndTouched(validationState.artefaktForm.form));
          }
        })
    ).subscribe();
  }

  public validateForms(artefakte: UIArtefakt[]) {
    artefakte.forEach(artefakt => {
      const stateItem = this._validationState.filter(item => item.identifikator === artefakt.Identifikator)[0];
      if (!!stateItem) {
        this.markControlsDirtyAndTouched(stateItem.artefaktForm.form);
      }
    });
  }

  public validateAllForms() {
    this._eventService.menuState$.pipe(
        take(1),
        tap(menuState => {
          const menuSeiten = getAllMenuStateSeiten(menuState);
          this._eventService.highestClickedMenuItem$.next(menuSeiten.length - 1);
        })
    ).subscribe();
    this._validationState.forEach((item: ValidationState) => {
      this.markControlsDirtyAndTouched(item.artefaktForm.form);
    });
  }

  public registerForm(artefakt: UiInputControl, formObject: NgForm): void {
    const obj = {} as ValidationState;
    obj.menuSeite = artefakt.menuseiteForValidationOnly;
    obj.artefaktForm = formObject;
    obj.identifikator = this._getKey(artefakt);
    obj.meldung = artefakt.UIMeldung;
    const validationStateItem = this._validationState.filter(
        item => item.identifikator === this._getKey(artefakt) && item.menuSeite === artefakt.menuseiteForValidationOnly)[0];
    if (!validationStateItem) {
      this._validationState.push(obj);
    } else {
      validationStateItem.menuSeite = obj.menuSeite;
      validationStateItem.artefaktForm = obj.artefaktForm;
    }
    const formStatusSubscription = formObject.valueChanges.subscribe(param => {
      this._updateValidationState();
    });
    this._formStatusSubscriptions.set(this._getKey(artefakt), formStatusSubscription);
    this._updateValidationState();
  }

  public isArtefakteValid(artefakte: UIArtefakt[]): boolean {
    for (const artefakt of artefakte) {
      const stateItem = this._validationState.filter(item => item.identifikator === artefakt.Identifikator)[0];
      if (!!stateItem && stateItem.artefaktForm.invalid) {
        return false;
      }
      if (!!stateItem && !!stateItem.meldung && !!stateItem.meldung.length) {
        return stateItem.meldung.filter(meldung => meldung.Meldungskategorie === ('Fehler')).length > 0;
      }
    }
    return true;
  }

  public validateGruppeTechnischeSteuerung(uiArtefakte: UIArtefakt[], gruppe: EnumGruppeTechnischeSteuerung): boolean{
    let result = true;
    const gruppenArtefakte = flatMap(uiArtefakte).filter((artefakt: UiInputControl) =>
        hasGruppeTechnischeSteuerung(artefakt, gruppe)).map(artefakt => artefakt.Identifikator);
    this._validationState.filter(item => gruppenArtefakte.includes(item.identifikator)).forEach(item =>{
      if (!item.artefaktForm.valid) {
        result = false;
      } else {
        Object.keys(item.artefaktForm.form.controls).forEach(key => {
          const artefaktControl = item.artefaktForm.form.controls[key];
          if (artefaktControl?.value?.hasOwnProperty('WertEingabe')) {
            result = !!artefaktControl.value['WertEingabe'];
          }
        });
      }
      if (!!item && !!item.meldung && !!item.meldung.length) {
        result = !item.meldung.filter(meldung => meldung.Meldungskategorie === ('Fehler')).length;
      }
      this.markControlsDirtyAndTouched(item.artefaktForm.form);
    });
    return result;
  }

  public markControlsDirtyAndTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(name => {
      const control = formGroup.controls[name];
      if (control instanceof FormGroup) {
        this.markControlsDirtyAndTouched(control);
      } else {
        control.markAsDirty();
        control.markAsTouched();
        control.updateValueAndValidity();
      }
    });
  }

  private _findKey(artefakt: UiInputControl, identifikator): UiInputControl {
    if (this._getKey(artefakt) === identifikator) {
      return artefakt;
    } else {
      if (!!artefakt.UIArtefakt) {
        for (const nestedArtefakt of artefakt.UIArtefakt) {
          const foundArtefakt = this._findKey(nestedArtefakt as UiInputControl, identifikator);
          if (!!foundArtefakt) {
            return foundArtefakt;
          }
        }
      }
    }
    return null;
  }

  private _cleanUpDeletedArtefact(arrayItem: ValidationState) {
    const subscription = this._formStatusSubscriptions.get(arrayItem.identifikator);
    if (!!subscription) {
      subscription.unsubscribe();
    }
    this._formStatusSubscriptions.delete(arrayItem.identifikator);
    this._validationState =
        this._validationState.filter(item => item.identifikator !== arrayItem.identifikator);
  }

  private _updateValidationState() {
    // die jeweilige Seite ist nur dann valid wenn alle darin enthaltenen
    // Forms valid sind und es kein artefakt.Meldung gibt
    const validSeitenMap: Map<string, boolean> = new Map();
    this._validationState.forEach(item => {
      if (!validSeitenMap.has(item.menuSeite)) {
        validSeitenMap.set(item.menuSeite, true);
      }
      if (item.artefaktForm.invalid) {
        validSeitenMap.set(item.menuSeite, false);
      }
      if (item.artefaktForm.valid && !!item.meldung && !!item.meldung.length) {
        if (item.meldung.filter(meldung => meldung.Meldungskategorie === ('Fehler')).length > 0) {
          validSeitenMap.set(item.menuSeite, false);
        }
      }
    });

    const menuValidationMap = this._setValidState(validSeitenMap);
    this._eventService.validationState$.next(menuValidationMap);

    if (!!this._menu && !!this._menu.UIMenuGruppen[0] && this._menu.UIMenuGruppen[0].UIMenuSeiten[0]) {
      // ToDo: Im Moment: Der Einfachheit halber wird angenommen, daß auf der ersten Seite im Menü
      // alle Eingaben sind, die den Tarif berechenbar machen
      if (validSeitenMap.has(this._menu.UIMenuGruppen[0].UIMenuSeiten[0].Identifikator)) {
        this.calcFormsValid$.next(validSeitenMap.get(this._menu.UIMenuGruppen[0].UIMenuSeiten[0].Identifikator));
      }
    }
  }

  private _setValidState(validSeitenMap: Map<string, boolean>): Map<string, boolean> {
    if (!this._menu) {
      return;
    }
    const menuValidationMap: Map<string, boolean> = new Map();
    this._menu.UIMenuGruppen.forEach(gruppe => {
      gruppe.UIMenuSeiten.forEach(seite => {
        menuValidationMap.set(seite.Identifikator, true);
        if ([...validSeitenMap.keys()].filter(item => item === seite.Identifikator).length) {
          menuValidationMap.set(seite.Identifikator, validSeitenMap.get(seite.Identifikator));
        }
      });
    });
    return menuValidationMap;
  }

  private _getKey(artefakt: UIArtefakt): string {
    const baseControl = artefakt as UiBaseControl;
    let key = baseControl.Identifikator;
    if (!!baseControl.ReferenzIdentifikator) {
      key = key + baseControl.ReferenzIdentifikator;
    }
    if (!!baseControl.ReferenzListItemKey) {
      key = key + baseControl.ReferenzListItemKey;
    }
    return key;
  }
}
